import React from 'react';
import { Box, Tooltip } from '@mui/material';

export type InlineEditorProps<V> = {
  value: V;
  onChange: (value: V) => void;
  onCommit: (value: V) => Promise<boolean> | void;
  onCancel: () => void;
  disabled: boolean;
  hasError: boolean;
  autoFocus: boolean;
  options?: { label: string; value: V }[];
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>;
  meta?: Record<string, unknown>;
};

export type EditableConfig<V> = {
  rawValue: V;
  onSave: (newValue: V) => Promise<void>;
  editor: (props: InlineEditorProps<V>) => React.ReactNode;
  validate?: (newValue: V) => string | null;
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>;
  meta?: Record<string, unknown>;
  closeOnCommit?: boolean;
};

export type ErasedEditable = {
  rawValue: unknown;
  onSave: (newValue: unknown) => Promise<void>;
  editor: (props: InlineEditorProps<unknown>) => React.ReactNode;
  validate?: (newValue: unknown) => string | null;
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>;
  meta?: Record<string, unknown>;
  closeOnCommit?: boolean;
};

export const editable = <V,>(config: EditableConfig<V>): ErasedEditable =>
  config as unknown as ErasedEditable;

export type InlineEditorOverridePatcher = (
  prev: Record<string, unknown>,
  v: unknown,
  saveFieldName: string,
  item: unknown,
) => Record<string, unknown>;

export type CustomInlineEditorEntry = {
  editor: (props: InlineEditorProps<unknown>) => React.ReactNode;
  patchOverrides?: InlineEditorOverridePatcher;
  closeOnCommit?: boolean;
};

export type CustomInlineEditors = Record<string, CustomInlineEditorEntry>;

export type Cell = {
  value?: React.ReactNode | ((raw: any) => React.ReactNode);
  isRowSelector?: boolean;
  sx?: React.CSSProperties;
  customCell?: React.ReactNode;
  doNotTruncate?: boolean;
  editable?: string | ErasedEditable;
  rawValue?: unknown;
  inlineOptions?: { label: string; value: string }[];
};

const MAX_CHARS = 50;
// Average character width in px at typical 13-14px UI font size
const CHAR_WIDTH_PX = 7;

const getEffectiveMaxChars = (columnWidthPx: number | undefined): number => {
  if (columnWidthPx == null) return MAX_CHARS;
  // Subtract cell horizontal padding (2 × 6px = 12px) before computing chars
  const usableWidth = Math.max(0, columnWidthPx - 12);
  const charsFromWidth = Math.floor(usableWidth / CHAR_WIDTH_PX);
  // When column is wider than the default MAX_CHARS threshold, let the
  // resized/default width be the sole limit (no artificial cap).
  return charsFromWidth;
};

const extractText = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) {
    const { children } = node.props as { children?: React.ReactNode };
    return extractText(children);
  }
  return '';
};

// Non-text elements (icons, etc.) are passed through unchanged.
const truncateNode = (node: React.ReactNode, budget: { remaining: number }): React.ReactNode => {
  if (typeof node === 'string' || typeof node === 'number') {
    if (budget.remaining <= 0) return null;
    const str = String(node);
    if (str.length <= budget.remaining) {
      budget.remaining -= str.length;
      return str;
    }
    const truncated = str.slice(0, budget.remaining) + '…';
    budget.remaining = 0;
    return truncated;
  }
  if (Array.isArray(node)) {
    return React.Children.map(node, child => truncateNode(child, budget));
  }
  if (React.isValidElement(node)) {
    const { children } = node.props as { children?: React.ReactNode };
    if (children === undefined || children === null) return node;
    const newChildren = truncateNode(children, budget);
    return React.cloneElement(node, node.props as any, newChildren);
  }
  return node;
};

type CellValueProps = { cell: Cell; columnWidthPx?: number };

const CellValue = ({ cell, columnWidthPx }: CellValueProps) => {
  const resolvedValue = typeof cell.value === 'function' ? cell.value(cell.rawValue) : cell.value;
  const { doNotTruncate } = cell;

  if (doNotTruncate) {
    return (
      <Box
        sx={{
          whiteSpace: 'normal',
          overflow: 'visible',
          wordBreak: 'break-word',
          width: '100%',
        }}
      >
        {resolvedValue}
      </Box>
    );
  }

  const effectiveMaxChars = getEffectiveMaxChars(columnWidthPx);
  const text = extractText(resolvedValue);
  const isTooLong = text.length > effectiveMaxChars;

  if (isTooLong) {
    const truncated = truncateNode(resolvedValue, { remaining: effectiveMaxChars });
    return (
      <Tooltip title={text} placement="top" arrow>
        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', cursor: 'default' }}>{truncated}</Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: cell.sx?.maxWidth ?? 'none',
      }}
    >
      {resolvedValue}
    </Box>
  );
};

export default CellValue;
